import {
    AppInsightsEventName,
    AppInsightsEventProperties,
    AppInsightsEvents,
    AppInsightsPropertyValue,
} from "./AppInsightsEvents";

/** Supported telemetry experiences for this application. */
type Experience = "candidate" | "hr";

/** Telemetry experience buckets used to tag emitted events. */
type UiTelemetryPayload = {
    eventName: string;
    experience: Experience | "unknown";
    path: string;
    url: string;
    sessionId: string;
    clientTimestampUtc: string;
    properties?: { [key: string]: AppInsightsPropertyValue };
};

/** Tracks whether telemetry initialization has already run. */
let started = false;
/** Current experience associated with outgoing telemetry. */
let currentExperience: Experience | "unknown" = "unknown";
/** Tracks whether global telemetry listeners are registered. */
let listenersRegistered = false;
/** Tracks whether the page exit event was already sent. */
let pageExitTracked = false;

/** Backend host used for UI telemetry posts. */
const backendApiHost = process.env.REACT_APP_SERVER_HOSTNAME || "";
/** Session id used to correlate telemetry events on this page. */
const telemetrySessionId = createTelemetrySessionId();

/**
 * Indicates whether UI telemetry is enabled for the current page.
 * @returns `true` when telemetry tracking should run.
 */
export function isTelemetryEnabled(): boolean {
    return true;
}

/**
 * Initializes UI telemetry once for the page lifetime.
 * @param experience - The active UI experience bucket for emitted events.
 * @returns `void`.
 */
export function initializeTelemetry(experience: Experience): void {
    currentExperience = experience;

    if (started) {
        return;
    }

    started = true;

    registerGlobalTelemetryListeners();

    trackEvent(AppInsightsEvents.ApplicationStarted, {
        experience,
        path: window.location.pathname,
        queryPresent: window.location.search.length > 0,
    });

    trackEvent(AppInsightsEvents.WebsiteVisited, {
        experience,
        path: window.location.pathname,
        queryPresent: window.location.search.length > 0,
    });
}

/**
 * Registers global listeners that capture errors, clicks, and page exit events.
 * @returns `void`.
 */
function registerGlobalTelemetryListeners(): void {
    if (listenersRegistered) {
        return;
    }

    listenersRegistered = true;

    window.addEventListener("error", (event) => {
        trackException(event.error ?? event.message, {
            eventName: AppInsightsEvents.UnhandledException,
            source: "window.error",
        });
    });

    window.addEventListener("unhandledrejection", (event) => {
        trackException(event.reason, {
            eventName: AppInsightsEvents.UnhandledException,
            source: "window.unhandledrejection",
        });
    });

    document.addEventListener(
        "click",
        (event) => {
            const target = event.target as Element | null;
            const clickable = target?.closest("button,[role='button']") as HTMLElement | null;

            if (!clickable) {
                return;
            }

            trackEvent(AppInsightsEvents.ButtonClicked, {
                elementTag: clickable.tagName.toLowerCase(),
                elementType: clickable.getAttribute("type") ?? "",
                elementRole: clickable.getAttribute("role") ?? "",
                elementId: clickable.id || "",
                ariaLabel: clickable.getAttribute("aria-label") ?? "",
                disabled: Boolean((clickable as HTMLButtonElement).disabled),
            });
        },
        true,
    );

    const trackExit = (reason: string) => {
        if (pageExitTracked) {
            return;
        }

        pageExitTracked = true;
        trackEvent(AppInsightsEvents.PageExited, {
            reason,
            path: window.location.pathname,
        });
    };

    window.addEventListener("pagehide", () => trackExit("pagehide"));
    window.addEventListener("beforeunload", () => trackExit("beforeunload"));
}

/**
 * Sends a custom event to the backend telemetry endpoint.
 * @param eventName - The telemetry event name to log.
 * @param properties - Optional event properties to include with the event.
 * @returns `void`.
 */
export function trackEvent(eventName: AppInsightsEventName, properties?: AppInsightsEventProperties): void {
    const sanitizedProperties = sanitizeProperties(properties);

    postBackendUiTelemetry(eventName, sanitizedProperties);
}

/**
 * Sends an exception event to the backend telemetry endpoint.
 * @param error - The thrown error-like value to normalize and track.
 * @param properties - Optional exception properties to include.
 * @returns `void`.
 */
export function trackException(error: unknown, properties?: AppInsightsEventProperties): void {
    const exception = normalizeUnknownError(error);
    const sanitizedProperties = sanitizeProperties(properties);

    postBackendUiTelemetry("ui.exception", {
        ...sanitizedProperties,
        message: exception.message,
        name: exception.name,
        stack: exception.stack || "",
    });
}

/**
 * Converts an unknown thrown value into a readable `Error` instance.
 * @param error - The unknown thrown value.
 * @returns A normalized `Error` with a best-effort message and name.
 */
function normalizeUnknownError(error: unknown): Error {
    if (error instanceof Error) {
        return error;
    }

    if (typeof error === "string") {
        return new Error(error);
    }

    if (error && typeof error === "object") {
        const asRecord = error as Record<string, unknown>;
        const message =
            getFirstString(asRecord, ["message", "error", "detail", "statusText"]) ||
            safeJsonStringify(error) ||
            "Unknown object error";

        const normalized = new Error(message);
        normalized.name = getFirstString(asRecord, ["name", "type", "code"]) || "Error";

        const raw = safeJsonStringify(error);
        if (raw && normalized.message !== raw) {
            normalized.stack = `${normalized.name}: ${normalized.message}\nRaw: ${raw}`;
        }

        return normalized;
    }

    return new Error(String(error));
}

/**
 * Returns the first non-empty string value found for the provided keys.
 * @param source - The source lookup object.
 * @param keys - Ordered keys to test.
 * @returns The first non-empty string value, or `undefined` when none exist.
 */
function getFirstString(source: Record<string, unknown>, keys: string[]): string | undefined {
    for (const key of keys) {
        const value = source[key];
        if (typeof value === "string" && value.trim().length > 0) {
            return value;
        }
    }

    return undefined;
}

/**
 * Safely stringifies a JSON-compatible value.
 * @param value - The value to stringify.
 * @returns The JSON string, or `undefined` when serialization fails.
 */
function safeJsonStringify(value: unknown): string | undefined {
    try {
        return JSON.stringify(value);
    } catch {
        return undefined;
    }
}

/**
 * Posts a UI telemetry event payload to the backend logging endpoint.
 * @param eventName - The event name to include in the payload.
 * @param properties - Optional event properties to include in the payload.
 * @returns `void`.
 */
function postBackendUiTelemetry(eventName: string, properties?: { [key: string]: AppInsightsPropertyValue }): void {
    try {
        const payload: UiTelemetryPayload = {
            eventName,
            experience: currentExperience,
            path: window.location.pathname,
            url: window.location.href,
            sessionId: telemetrySessionId,
            clientTimestampUtc: new Date().toISOString(),
            properties,
        };

        void fetch(`${backendApiHost}/api/uiTelemetry/events`, {
            method: "POST",
            headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
            keepalive: true,
        }).catch(() => {
            // Do not break user workflows if backend telemetry logging fails.
        });
    } catch {
        // Do not break user workflows if telemetry payload creation or posting fails.
    }
}

/**
 * Creates a page-scoped identifier used to correlate telemetry events.
 * @returns A session identifier string.
 */
function createTelemetrySessionId(): string {
    try {
        if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
            return crypto.randomUUID();
        }
    } catch {
        // Ignore and fall back to a simple generated value.
    }

    return `session-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Converts event properties to primitive values supported by telemetry custom properties.
 * @param properties - Optional event properties to sanitize.
 * @returns Sanitized properties, or `undefined` when no values remain.
 */
function sanitizeProperties(
    properties?: AppInsightsEventProperties,
): { [key: string]: AppInsightsPropertyValue } | undefined {
    if (!properties) {
        return undefined;
    }

    const sanitized = Object.entries(properties).reduce<{ [key: string]: AppInsightsPropertyValue }>(
        (all, [key, value]) => {
            if (value === null || value === undefined) {
                return all;
            }

            switch (typeof value) {
                case "string":
                case "number":
                case "boolean":
                    all[key] = value;
                    return all;
                default:
                    all[key] = String(value);
                    return all;
            }
        },
        {},
    );

    return Object.keys(sanitized).length > 0 ? sanitized : undefined;
}
